import React, { useEffect, useState } from 'react'
import { WContainer } from '@next-nx/shared-ui/atoms'
import {
  Hero,
  LandingCta,
  LandingHeader,
  LandingOfferteSection,
  LandingPreventivoBox,
  LandingServiziMobilitaSection,
  LandingServiziSection,
  LandingDisclaimer,
  LandingFooter,
  LandingReassurance,
  Page,
  FormRichiestaPreventivoModal,
  RichiestaPreventivoThankYouModal,
  FormRichiestaPreventivoInPagina,
} from '@next-nx/shared-ui/organisms'
import styles from './LandingTemplate.module.sass'
import { useSelector } from 'react-redux'
import { LandingType, TypDataTracking } from '@next-nx/utils/types'
import { useFindMenu, useSite, useTealium } from '@next-nx/hooks'
import { useCreateRichiestaContattoMutation } from '@next-nx/shared-graphql/unipolrental'
import { getTimestamp } from '@next-nx/utils'
import { AGENZIE, CARROZZERIE, NUOVAMOBILITA, SOCICOOP } from '@next-nx/utils/settings'

interface Props {
  variant?: 'modal' | 'in_pagina'
  className?: string
  pagina: any
  utenteCarrozzeria?: string
}

const LandingTemplate = (props: Props) => {
  const { landing }: { landing: LandingType } = useSelector((state: any) => state?.landing)

  const [createRichiestaContattoMutation] = useCreateRichiestaContattoMutation()

  const { variant = 'modal', className = '', pagina = {}, utenteCarrozzeria = '' } = props

  const [openFormRichiestaPreventivoModal, setOpenFormRichiestaPreventivoModal] = useState(false)
  const [openRichiestaPreventivoThankYouModal, setOpenRichiestaPreventivoThankYouModal] =
    useState(false)
  const [statusRichiestaPreventivoThankYouModal, setStatusRichiestaPreventivoThankYouModal] =
    useState<string | undefined>()

  const [trackingData, setTrackingData] = useState<TypDataTracking>()

  const heroMenu = useFindMenu('hero')
  const preventivoMenu = useFindMenu('preventivo')

  const offertaInEvidenza = landing?.offertaInEvidenza
  const offerte = landing?.offerte?.edges.map((item) => item.node) || []
  const siteId = landing?.siteId?.toString()
  const site = useSite(siteId)
  const serviziMenu = useFindMenu('servizi')
  const serviziMobilitaMenu = useFindMenu('servizi-mobilita')
  const ctaMenu = useFindMenu('cta')
  const reassuranceMenu = useFindMenu('reassurance')
  const disclaimerMenu = useFindMenu('disclaimer')

  const { linkDataLayer, getListingProdottiData } = useTealium()

  const form_id = 'form_in_pagina'

  const handleClickScroll = () => {
    const element = document?.getElementById(form_id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleTracking = () => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: 'action',
      action_name: 'items_view',
      action_tech: 'view',
      action_type: 'carosello',
      action_timestamp,
      action_effect: 'visualizzazione_prodotti',
      view_items: getListingProdottiData(offerte, 'privati', 'landing', true),
    })
  }

  useEffect(() => {
    site !== NUOVAMOBILITA && site !== AGENZIE && site !== CARROZZERIE && handleTracking()
  }, [])

  return (
    <Page
      pagina={pagina}
      header={
        <LandingHeader
          onClick={
            variant === 'modal' ? () => setOpenFormRichiestaPreventivoModal(true) : undefined
          }
          variant={variant}
          className={styles.header}
        />
      }
      prefooter={false}
      footer={<LandingFooter className={styles.footer} />}
      className={`${styles.landingTemplate} ${className}`}
    >
      <Hero
        item={heroMenu}
        site={site}
        contentClassName={site === SOCICOOP || site === NUOVAMOBILITA ? styles.heroContent : ''}
      />
      <WContainer>
        {variant === 'modal' ? (
          <LandingPreventivoBox
            item={preventivoMenu}
            onClick={() => setOpenFormRichiestaPreventivoModal(true)}
            className={styles.preventivoBox}
          />
        ) : (
          ''
        )}
        {variant === 'modal' ? (
          <LandingOfferteSection
            offertaInEvidenza={offertaInEvidenza}
            offerte={offerte}
            className={styles.offerteSection}
          />
        ) : (
          ''
        )}
        {variant === 'modal' ? (
          <LandingServiziSection
            item={serviziMenu}
            onClick={() => setOpenFormRichiestaPreventivoModal(true)}
            className={styles.serviziSection}
            variant={variant}
          />
        ) : (
          <div className={styles.landingInPagina}>
            <LandingServiziSection
              item={serviziMenu}
              onClick={() => setOpenFormRichiestaPreventivoModal(true)}
              className={styles.serviziSection}
              variant={variant}
            />
            <FormRichiestaPreventivoInPagina
              id={form_id} // serve per ScrollToElement
              mutation={createRichiestaContattoMutation}
              onSubmit={(status) => {
                setOpenFormRichiestaPreventivoModal(false)
                setOpenRichiestaPreventivoThankYouModal(true)
                setStatusRichiestaPreventivoThankYouModal(status)
              }}
              utenteCarrozzeria={utenteCarrozzeria}
              setTrackingData={setTrackingData} // Per tracciamenti in modale ThankYou
            />
          </div>
        )}
      </WContainer>
      <LandingServiziMobilitaSection
        item={serviziMobilitaMenu}
        className={styles.serviziMobilitaSection}
      />
      <WContainer>
        <LandingCta
          item={ctaMenu}
          onClick={
            variant === 'modal'
              ? () => setOpenFormRichiestaPreventivoModal(true)
              : handleClickScroll
          }
        />
        <LandingReassurance item={reassuranceMenu} className={styles.reassurance} />
      </WContainer>
      <WContainer variant="sm">
        <LandingDisclaimer item={disclaimerMenu} className={styles.disclaimer} />
      </WContainer>
      {variant === 'in_pagina' ? (
        ''
      ) : (
        <FormRichiestaPreventivoModal
          open={openFormRichiestaPreventivoModal}
          setOpen={setOpenFormRichiestaPreventivoModal}
          mutation={createRichiestaContattoMutation}
          onSubmit={(status) => {
            setOpenFormRichiestaPreventivoModal(false)
            setOpenRichiestaPreventivoThankYouModal(true)
            setStatusRichiestaPreventivoThankYouModal(status)
          }}
        />
      )}
      <RichiestaPreventivoThankYouModal
        open={openRichiestaPreventivoThankYouModal}
        setOpen={setOpenRichiestaPreventivoThankYouModal}
        status={statusRichiestaPreventivoThankYouModal}
        trackingData={trackingData}
      />
    </Page>
  )
}

export default LandingTemplate
